import { z } from 'zod';

/**
 * The password policy, declared once and enforced identically by the api and by
 * both clients' form validation (U3).
 *
 * Length is the requirement that actually correlates with strength, so the
 * floor is 10 rather than the customary 8, and there is no composition rule
 * (one-uppercase-one-digit-one-symbol) — those push people towards
 * `Password1!` and are no longer recommended. The ceiling exists because argon2
 * hashes whatever it is given: an unbounded password is a cheap way to burn
 * server memory.
 */
export const MIN_PASSWORD_LENGTH = 10;
export const MAX_PASSWORD_LENGTH = 128;

export const PasswordSchema = z
  .string()
  .min(MIN_PASSWORD_LENGTH, `Password must be at least ${MIN_PASSWORD_LENGTH} characters`)
  .max(MAX_PASSWORD_LENGTH, `Password must be at most ${MAX_PASSWORD_LENGTH} characters`);

/**
 * Email as stored and compared: trimmed and lower-cased, so `A@b.com` and
 * `a@b.com` are one account. Normalization lives in the schema rather than in a
 * service, so every entry point normalizes the same way.
 */
export const EmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email())
  .pipe(z.string().max(254));

export const ChangePasswordSchema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: PasswordSchema,
  })
  .strict();

export type ChangePasswordRequest = z.infer<typeof ChangePasswordSchema>;
